import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Download, Info, Moon, Sun, X, FileText, File, Loader2 } from "lucide-react";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd, DesktopOnlyAd } from "../components/AdPlacement";

// Template component for document to PDF conversion
function DocumentToPdf({ 
  format, 
  formatName, 
  acceptTypes, 
  color = "red",
  description,
  icon: Icon = FileText
}) {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentFile, setCurrentFile] = useState("");
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const colorClasses = {
    red: { bg: "from-red-500 to-red-600", text: "text-red-500", hover: "bg-red-600 hover:bg-red-700" },
    blue: { bg: "from-blue-500 to-blue-600", text: "text-blue-500", hover: "bg-blue-600 hover:bg-blue-700" },
    green: { bg: "from-green-500 to-green-600", text: "text-green-500", hover: "bg-green-600 hover:bg-green-700" },
    purple: { bg: "from-purple-500 to-purple-600", text: "text-purple-500", hover: "bg-purple-600 hover:bg-purple-700" },
    orange: { bg: "from-orange-500 to-orange-600", text: "text-orange-500", hover: "bg-orange-600 hover:bg-orange-700" },
    cyan: { bg: "from-cyan-500 to-cyan-600", text: "text-cyan-500", hover: "bg-cyan-600 hover:bg-cyan-700" },
    emerald: { bg: "from-emerald-500 to-emerald-600", text: "text-emerald-500", hover: "bg-emerald-600 hover:bg-emerald-700" },
    amber: { bg: "from-amber-500 to-amber-600", text: "text-amber-500", hover: "bg-amber-600 hover:bg-amber-700" },
  };
  const colors = colorClasses[color] || colorClasses.red;

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = `${formatName} to PDF - Convert ${formatName} to PDF - KayTool`;
  }, [formatName]);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;
    
    const newFiles = selectedFiles.map((file, i) => ({
      id: Date.now() + i,
      name: file.name,
      file: file,
      size: file.size,
      status: 'pending', // pending, converting, done, error
      pdfUrl: null,
    }));
    
    setFiles(prev => [...prev, ...newFiles]);
    toast.success(`Added ${newFiles.length} file${newFiles.length > 1 ? 's' : ''}`);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length > 0) {
      const newFiles = droppedFiles.map((file, i) => ({
        id: Date.now() + i,
        name: file.name,
        file: file,
        size: file.size,
        status: 'pending',
        pdfUrl: null,
      }));
      setFiles(prev => [...prev, ...newFiles]);
      toast.success(`Added ${newFiles.length} file${newFiles.length > 1 ? 's' : ''}`);
    }
  };

  const convertToPdf = async () => {
    if (files.length === 0) {
      toast.error(`Please add ${formatName} files first`);
      return;
    }

    setIsProcessing(true);
    setProgress(0);

    const pendingFiles = files.filter(f => f.status === 'pending' || f.status === 'error');
    
    for (let i = 0; i < pendingFiles.length; i++) {
      const fileItem = pendingFiles[i];
      setCurrentFile(fileItem.name);
      
      // Update status to converting
      setFiles(prev => prev.map(f => 
        f.id === fileItem.id ? { ...f, status: 'converting' } : f
      ));

      try {
        const formData = new FormData();
        formData.append('file', fileItem.file);

        const response = await fetch(`${API_URL}/api/document-to-pdf/convert`, {
          method: 'POST',
          body: formData,
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Conversion failed');
        }

        // Get the PDF blob
        const blob = await response.blob();
        const pdfUrl = URL.createObjectURL(blob);
        
        // Get filename from Content-Disposition header or generate one
        const contentDisposition = response.headers.get('Content-Disposition');
        let pdfName = fileItem.name.replace(/\.[^/.]+$/, '.pdf');
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/);
          if (match) pdfName = match[1];
        }

        // Update file status
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'done', pdfUrl, pdfName } : f
        ));

        // Auto-download the PDF
        const a = document.createElement('a');
        a.href = pdfUrl;
        a.download = pdfName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);

      } catch (error) {
        console.error('Conversion error:', error);
        setFiles(prev => prev.map(f => 
          f.id === fileItem.id ? { ...f, status: 'error', error: error.message } : f
        ));
        toast.error(`Failed to convert ${fileItem.name}: ${error.message}`);
      }

      setProgress(Math.round(((i + 1) / pendingFiles.length) * 100));
    }

    setIsProcessing(false);
    setCurrentFile("");
    
    const successCount = files.filter(f => f.status === 'done').length;
    if (successCount > 0) {
      toast.success(`Converted ${successCount} file${successCount > 1 ? 's' : ''} to PDF!`);
    }
  };

  const downloadPdf = (fileItem) => {
    if (!fileItem.pdfUrl) return;
    const a = document.createElement('a');
    a.href = fileItem.pdfUrl;
    a.download = fileItem.pdfName || fileItem.name.replace(/\.[^/.]+$/, '.pdf');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const removeFile = (id) => {
    setFiles(prev => prev.filter(f => f.id !== id));
  };

  const clearAll = () => {
    setFiles([]);
  };

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>;
      case 'converting':
        return <Badge className="bg-blue-500">Converting...</Badge>;
      case 'done':
        return <Badge className="bg-green-500">Done</Badge>;
      case 'error':
        return <Badge variant="destructive">Error</Badge>;
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-slate-50 to-slate-100'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colors.bg} flex items-center justify-center`}>
              <Icon className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> Secure Conversion
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className={`inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r ${colors.bg} text-white rounded-full mb-4`}>
            <Icon className="w-5 h-5" />
            <span className="font-medium">{formatName} to PDF</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Convert {formatName} to PDF
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            {description || `Convert your ${formatName} files to PDF format. Fast, secure, and free.`}
          </p>
        </div>

        {/* Upload Area */}
        <Card 
          className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
            isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-600'
          }`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <CardContent className="p-12 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept={acceptTypes}
              multiple
              onChange={handleFileSelect}
              className="hidden"
              data-testid="file-input"
            />
            <div 
              className="cursor-pointer"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-zone"
            >
              <Upload className={`w-16 h-16 mx-auto mb-4 ${colors.text}`} />
              <h3 className="text-xl font-semibold text-slate-700 dark:text-white mb-2">
                Drop {formatName} files or click to upload
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Files are converted securely on our server
              </p>
              <Badge variant="secondary">Multiple files supported</Badge>
            </div>
          </CardContent>
        </Card>

        {isProcessing && (
          <Card className="mt-6 dark:bg-slate-800">
            <CardContent className="p-6">
              <div className="flex items-center gap-4 mb-2">
                <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                <span className="text-sm text-slate-600 dark:text-slate-300">
                  Converting: {currentFile}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Progress value={progress} className="h-3" />
                </div>
                <span className="text-sm font-medium">{progress}%</span>
              </div>
            </CardContent>
          </Card>
        )}

        {files.length > 0 && (
          <div className="mt-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                Files ({files.length})
              </h3>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={clearAll} className="text-red-600">
                  <X className="w-4 h-4 mr-1" /> Clear All
                </Button>
                <Button 
                  onClick={convertToPdf} 
                  disabled={isProcessing || files.every(f => f.status === 'done')}
                  data-testid="convert-button"
                  className={`bg-gradient-to-r ${colors.bg} hover:opacity-90`}
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Converting...
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4 mr-2" />
                      Convert to PDF
                    </>
                  )}
                </Button>
              </div>
            </div>

            <DesktopOnlyAd slot="above-results" size="large-rectangle" className="my-4" />

            <div className="space-y-3">
              {files.map((fileItem) => (
                <Card key={fileItem.id} className="dark:bg-slate-800">
                  <CardContent className="p-4 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                      <File className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 dark:text-white truncate">{fileItem.name}</p>
                      <p className="text-sm text-slate-500">{formatSize(fileItem.size)}</p>
                      {fileItem.error && (
                        <p className="text-sm text-red-500">{fileItem.error}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      {getStatusBadge(fileItem.status)}
                      {fileItem.status === 'done' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => downloadPdf(fileItem)}
                        >
                          <Download className="w-4 h-4 mr-1" /> PDF
                        </Button>
                      )}
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600"
                        onClick={() => removeFile(fileItem.id)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>

        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: Upload, title: "Easy Upload", desc: "Drag & drop or click to upload files" },
            { icon: FileText, title: "Fast Conversion", desc: "Convert documents to PDF in seconds" },
            { icon: Download, title: "Instant Download", desc: "Download converted PDFs immediately" }
          ].map((feature, i) => (
            <Card key={i} className="dark:bg-slate-800">
              <CardContent className="p-6 text-center">
                <feature.icon className={`w-10 h-10 mx-auto mb-3 ${colors.text}`} />
                <h4 className="font-semibold mb-2 dark:text-white">{feature.title}</h4>
                <p className="text-sm text-slate-600 dark:text-slate-400">{feature.desc}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. Your files are processed securely.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

// Export individual converters
export const WordToPdf = () => (
  <DocumentToPdf 
    format="docx" 
    formatName="Word" 
    acceptTypes=".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    color="blue"
    description="Convert Microsoft Word documents (DOC, DOCX) to PDF format. Preserves formatting and layout."
  />
);

export const DocxToPdf = () => (
  <DocumentToPdf 
    format="docx" 
    formatName="DOCX" 
    acceptTypes=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    color="blue"
    description="Convert DOCX documents to PDF format. Modern Word format to universal PDF."
  />
);

export const DocToPdf = () => (
  <DocumentToPdf 
    format="doc" 
    formatName="DOC" 
    acceptTypes=".doc,application/msword"
    color="blue"
    description="Convert legacy DOC documents to PDF format. Classic Word format support."
  />
);

export const PowerpointToPdf = () => (
  <DocumentToPdf 
    format="pptx" 
    formatName="PowerPoint" 
    acceptTypes=".ppt,.pptx,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
    color="orange"
    description="Convert PowerPoint presentations (PPT, PPTX) to PDF format. Perfect for sharing slides."
  />
);

export const PptxToPdf = () => (
  <DocumentToPdf 
    format="pptx" 
    formatName="PPTX" 
    acceptTypes=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
    color="orange"
    description="Convert PPTX presentations to PDF. Modern PowerPoint format."
  />
);

export const PptToPdf = () => (
  <DocumentToPdf 
    format="ppt" 
    formatName="PPT" 
    acceptTypes=".ppt,application/vnd.ms-powerpoint"
    color="orange"
    description="Convert legacy PPT presentations to PDF format."
  />
);

export const ExcelToPdf = () => (
  <DocumentToPdf 
    format="xlsx" 
    formatName="Excel" 
    acceptTypes=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    color="green"
    description="Convert Excel spreadsheets (XLS, XLSX) to PDF format. Preserves tables and formatting."
  />
);

export const XlsxToPdf = () => (
  <DocumentToPdf 
    format="xlsx" 
    formatName="XLSX" 
    acceptTypes=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    color="green"
    description="Convert XLSX spreadsheets to PDF. Modern Excel format."
  />
);

export const XlsToPdf = () => (
  <DocumentToPdf 
    format="xls" 
    formatName="XLS" 
    acceptTypes=".xls,application/vnd.ms-excel"
    color="green"
    description="Convert legacy XLS spreadsheets to PDF format."
  />
);

export const OdtToPdf = () => (
  <DocumentToPdf 
    format="odt" 
    formatName="ODT" 
    acceptTypes=".odt,application/vnd.oasis.opendocument.text"
    color="cyan"
    description="Convert OpenDocument Text files to PDF format."
  />
);

export const OdpToPdf = () => (
  <DocumentToPdf 
    format="odp" 
    formatName="ODP" 
    acceptTypes=".odp,application/vnd.oasis.opendocument.presentation"
    color="cyan"
    description="Convert OpenDocument Presentation files to PDF format."
  />
);

export const OdsToPdf = () => (
  <DocumentToPdf 
    format="ods" 
    formatName="ODS" 
    acceptTypes=".ods,application/vnd.oasis.opendocument.spreadsheet"
    color="cyan"
    description="Convert OpenDocument Spreadsheet files to PDF format."
  />
);

export const OdgToPdf = () => (
  <DocumentToPdf 
    format="odg" 
    formatName="ODG" 
    acceptTypes=".odg,application/vnd.oasis.opendocument.graphics"
    color="cyan"
    description="Convert OpenDocument Graphics files to PDF format."
  />
);

export const RtfToPdf = () => (
  <DocumentToPdf 
    format="rtf" 
    formatName="RTF" 
    acceptTypes=".rtf,application/rtf"
    color="purple"
    description="Convert Rich Text Format documents to PDF."
  />
);

export const EpubToPdf = () => (
  <DocumentToPdf 
    format="epub" 
    formatName="EPUB" 
    acceptTypes=".epub,application/epub+zip"
    color="amber"
    description="Convert EPUB e-books to PDF format for easy printing and sharing."
  />
);

export default DocumentToPdf;
